import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
  MinusCircleIcon,
  LockClosedIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import ProviderTabs from '../../components/common/ProviderTabs';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { microsoftAPI, googleAPI, oauthAPI } from '../../services/api';

const osIcons = {
  Windows: ComputerDesktopIcon,
  macOS: ComputerDesktopIcon,
  iOS: DevicePhoneMobileIcon,
  Android: DevicePhoneMobileIcon,
  ChromeOS: ComputerDesktopIcon,
  Mobile: DevicePhoneMobileIcon,
  default: DevicePhoneMobileIcon,
};

const complianceColors = {
  compliant: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-400',
    icon: CheckCircleIcon,
  },
  noncompliant: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-400',
    icon: XCircleIcon,
  },
  unknown: {
    bg: 'bg-slate-100 dark:bg-slate-700',
    text: 'text-slate-600 dark:text-slate-400',
    icon: ExclamationTriangleIcon,
  },
};

// Google device status colors
const googleStatusColors = {
  active: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-700 dark:text-green-400',
    icon: CheckCircleIcon,
  },
  suspended: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-700 dark:text-amber-400',
    icon: MinusCircleIcon,
  },
  disabled: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    text: 'text-red-700 dark:text-red-400',
    icon: XCircleIcon,
  },
  unknown: {
    bg: 'bg-slate-100 dark:bg-slate-700',
    text: 'text-slate-600 dark:text-slate-400',
    icon: ExclamationTriangleIcon,
  },
};

export default function DeviceInventoryPage() {
  const [devices, setDevices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [osFilter, setOsFilter] = useState('');
  const [search, setSearch] = useState('');
  const [microsoftConnected, setMicrosoftConnected] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [activeProvider, setActiveProvider] = useState(null);
  const [intuneAvailable, setIntuneAvailable] = useState(true);
  const [intuneMessage, setIntuneMessage] = useState('');

  useEffect(() => {
    checkConnections();
  }, []);

  const checkConnections = async () => {
    try {
      const response = await oauthAPI.getStatus();
      const msConnected = response.data.microsoft?.connected || false;
      const gConnected = response.data.google?.connected || false;

      setMicrosoftConnected(msConnected);
      setGoogleConnected(gConnected);

      // Set default active provider based on what's connected
      if (msConnected) {
        setActiveProvider('microsoft');
      } else if (gConnected) {
        setActiveProvider('google');
      } else {
        setLoading(false);
      }
    } catch (err) {
      setError('Failed to check connection status');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeProvider) {
      if (activeProvider === 'microsoft') {
        checkIntuneAndLoadData();
      } else {
        setIntuneAvailable(true); // Reset for Google
        loadData();
      }
    }
  }, [activeProvider]);

  const checkIntuneAndLoadData = async () => {
    try {
      const intuneStatus = await microsoftAPI.checkIntuneStatus();
      if (!intuneStatus.data.data.available) {
        setIntuneAvailable(false);
        setIntuneMessage(intuneStatus.data.data.reason || 'Intune is not available for this tenant.');
        setLoading(false);
        return;
      }
      setIntuneAvailable(true);
      await loadData();
    } catch (err) {
      const errorMessage = err.response?.data?.error?.toLowerCase() || '';
      // Check for license-related errors
      if (
        errorMessage.includes('not applicable') ||
        errorMessage.includes('forbidden') ||
        errorMessage.includes('permission denied') ||
        errorMessage.includes('not licensed') ||
        errorMessage.includes('not entitled')
      ) {
        setIntuneAvailable(false);
        setIntuneMessage('Device inventory requires Microsoft Intune (M365 Business Premium or Enterprise with Intune).');
        setLoading(false);
        return;
      }
      setError('Failed to check Intune status');
      setLoading(false);
    }
  };

  const getAPI = () => {
    return activeProvider === 'google' ? googleAPI : microsoftAPI;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const api = getAPI();
      const params = {};

      if (activeProvider === 'google') {
        if (filter !== 'all') params.status = filter;
        if (osFilter) params.deviceType = osFilter;
      } else {
        if (filter !== 'all') params.filter = filter;
        if (osFilter) params.osType = osFilter;
      }
      if (search) params.search = search;

      const [devicesRes, summaryRes] = await Promise.all([
        api.getDevices(params),
        api.getDeviceSummary(),
      ]);

      const fetchedDevices = devicesRes.data.data.devices;
      const fetchedSummary = summaryRes.data.data;
      setDevices(fetchedDevices);
      setSummary(fetchedSummary);

      // Auto-sync on first visit if no device data exists yet
      if (fetchedDevices.length === 0 && (fetchedSummary?.totalDevices === 0 || fetchedSummary?.totalDevices === undefined) && !syncing) {
        setSyncing(true);
        try {
          const api = getAPI();
          await api.triggerSync('devices');
          const [newDevicesRes, newSummaryRes] = await Promise.all([
            api.getDevices(params),
            api.getDeviceSummary(),
          ]);
          setDevices(newDevicesRes.data.data.devices);
          setSummary(newSummaryRes.data.data);
        } catch (syncErr) {
          // Silently ignore auto-sync failure - user can still click Sync Now
        } finally {
          setSyncing(false);
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to load device data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      const api = getAPI();
      await api.triggerSync('devices');
      await loadData();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Sync failed';
      setError(errorMessage);
    } finally {
      setSyncing(false);
    }
  };

  const handleProviderChange = (provider) => {
    setActiveProvider(provider);
    setFilter('all');
    setOsFilter('');
    setSearch('');
  };

  useEffect(() => {
    if (activeProvider && (microsoftConnected || googleConnected)) {
      const isCurrentProviderConnected =
        (activeProvider === 'microsoft' && microsoftConnected) ||
        (activeProvider === 'google' && googleConnected);

      if (isCurrentProviderConnected) {
        if (activeProvider === 'microsoft' && intuneAvailable) {
          loadData();
        } else if (activeProvider === 'google') {
          loadData();
        }
      }
    }
  }, [filter, osFilter, search]);

  const getComplianceStyle = (state) => {
    return complianceColors[state] || complianceColors.unknown;
  };

  const getGoogleStatusStyle = (status) => {
    return googleStatusColors[status] || googleStatusColors.unknown;
  };

  const getOsIcon = (osType) => {
    return osIcons[osType] || osIcons.default;
  };

  const isGoogle = activeProvider === 'google';

  if (!microsoftConnected && !googleConnected && !loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Device Inventory</h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            View managed devices from your cloud providers.
          </p>
        </div>

        <Card className="text-center py-12">
          <ExclamationTriangleIcon className="h-12 w-12 mx-auto text-amber-500 mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
            No Cloud Provider Connected
          </h3>
          <p className="text-slate-500 dark:text-slate-400 mb-6">
            Connect Microsoft 365 or Google Workspace to view device inventory.
          </p>
          <Link to="/dashboard/connect">
            <Button>Connect Cloud Provider</Button>
          </Link>
        </Card>
      </div>
    );
  }

  // Show Intune not available message only for Microsoft
  if (activeProvider === 'microsoft' && !intuneAvailable && !loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Device Inventory</h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            View managed devices from your cloud providers.
          </p>
        </div>

        <ProviderTabs
          activeProvider={activeProvider}
          onProviderChange={handleProviderChange}
          microsoftConnected={microsoftConnected}
          googleConnected={googleConnected}
        />

        <Card className="py-8">
          <div className="flex flex-col items-center text-center max-w-lg mx-auto">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30 mb-4">
              <LockClosedIcon className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
              Device Inventory Unavailable
            </h3>
            <p className="text-slate-500 dark:text-slate-400 mb-6">
              Device inventory requires Microsoft Intune, which is included in M365 Business Premium or Enterprise plans with Intune.
            </p>

            <div className="w-full bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 text-left">
              <div className="flex items-start gap-3">
                <InformationCircleIcon className="h-5 w-5 text-primary-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-slate-900 dark:text-white mb-2">
                    How to Enable Device Inventory
                  </h4>
                  <ol className="text-sm text-slate-600 dark:text-slate-400 space-y-2 list-decimal list-inside">
                    <li>Upgrade to <strong>Microsoft 365 Business Premium</strong> or an <strong>Enterprise plan with Intune</strong></li>
                    <li>Go to the <a href="https://intune.microsoft.com" target="_blank" rel="noopener noreferrer" className="text-primary-600 dark:text-primary-400 hover:underline">Microsoft Intune admin center</a></li>
                    <li>Configure device enrollment policies</li>
                    <li>Enroll devices (Windows, macOS, iOS, Android)</li>
                    <li>Return here and click "Sync Now" to see your devices</li>
                  </ol>
                  <p className="mt-3 text-xs text-slate-500 dark:text-slate-500">
                    <a
                      href="https://learn.microsoft.com/en-us/mem/intune/fundamentals/what-is-intune"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      Learn more about Microsoft Intune →
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Show Google tab hint if connected */}
        {googleConnected && (
          <div className="text-center">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Tip: You can view Google Workspace devices by switching to the Google tab above.
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Device Inventory</h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            {isGoogle
              ? 'View mobile and Chrome OS devices from Google Workspace.'
              : 'View Intune-enrolled devices from Microsoft 365.'}
          </p>
        </div>
        <Button onClick={handleSync} loading={syncing} disabled={syncing || !activeProvider}>
          <ArrowPathIcon className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync Now'}
        </Button>
      </div>

      {/* Provider Tabs */}
      <ProviderTabs
        activeProvider={activeProvider}
        onProviderChange={handleProviderChange}
        microsoftConnected={microsoftConnected}
        googleConnected={googleConnected}
      />

      {/* Error Banner */}
      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-700">
                <ComputerDesktopIcon className="h-6 w-6 text-slate-600 dark:text-slate-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total Devices</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                  {summary.totalDevices}
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30">
                <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {isGoogle ? 'Active' : 'Compliant'}
                </p>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {isGoogle ? summary.activeDevices : summary.compliantDevices}
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/30">
                <XCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {isGoogle ? 'Suspended/Disabled' : 'Non-Compliant'}
                </p>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {isGoogle
                    ? (summary.suspendedDevices || 0) + (summary.disabledDevices || 0)
                    : summary.nonCompliantDevices}
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
                <CheckCircleIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {isGoogle ? 'Active Rate' : 'Compliance Rate'}
                </p>
                <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                  {isGoogle
                    ? summary.totalDevices > 0
                      ? Math.round((summary.activeDevices / summary.totalDevices) * 100)
                      : 0
                    : summary.compliancePercentage}%
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Device Type Distribution */}
      {summary && (isGoogle ? summary.byDeviceType : summary.byOperatingSystem) && (
        <Card>
          <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
            {isGoogle ? 'Devices by Type' : 'Devices by Operating System'}
          </h3>
          <div className="flex flex-wrap gap-3">
            {Object.entries(isGoogle ? summary.byDeviceType : summary.byOperatingSystem).map(([key, count]) => (
              <div
                key={key}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-700"
              >
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{key}</span>
                <span className="text-sm text-slate-500 dark:text-slate-400">{count}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Filters and Search */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search by device name or user..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-slate-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Devices</option>
              {isGoogle ? (
                <>
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="disabled">Disabled</option>
                </>
              ) : (
                <>
                  <option value="compliant">Compliant</option>
                  <option value="noncompliant">Non-Compliant</option>
                </>
              )}
            </select>
            <select
              value={osFilter}
              onChange={(e) => setOsFilter(e.target.value)}
              className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {isGoogle ? (
                <>
                  <option value="">All Types</option>
                  <option value="iOS">iOS</option>
                  <option value="Android">Android</option>
                  <option value="ChromeOS">Chrome OS</option>
                </>
              ) : (
                <>
                  <option value="">All OS</option>
                  <option value="Windows">Windows</option>
                  <option value="macOS">macOS</option>
                  <option value="iOS">iOS</option>
                  <option value="Android">Android</option>
                </>
              )}
            </select>
          </div>
        </div>
      </Card>

      {/* Last Sync Info */}
      {summary?.lastSyncedAt && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Last synced: {new Date(summary.lastSyncedAt).toLocaleString()}
        </p>
      )}

      {/* Devices Table */}
      <Card padding={false}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="md" text="Loading devices..." />
          </div>
        ) : devices.length === 0 ? (
          <div className="text-center py-12">
            <ComputerDesktopIcon className="h-12 w-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-500 dark:text-slate-400">
              {summary?.totalDevices === 0
                ? `No devices found. Click "Sync Now" to fetch data from ${isGoogle ? 'Google Workspace' : 'Intune'}.`
                : 'No devices match your search criteria.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Device
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {isGoogle ? 'Type' : 'OS'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    {isGoogle ? 'Status' : 'Compliance'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Last Sync
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {devices.map((device) => {
                  const OsIcon = getOsIcon(isGoogle ? device.deviceType : device.osType);
                  const statusStyle = isGoogle
                    ? getGoogleStatusStyle(device.status)
                    : getComplianceStyle(device.complianceState);
                  const StatusIcon = statusStyle.icon;

                  return (
                    <tr key={device.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                            <OsIcon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">
                              {device.deviceName || 'Unknown Device'}
                            </p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              {device.manufacturer} {device.model}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-slate-900 dark:text-white">
                          {isGoogle ? device.userEmail || '-' : device.userDisplayName || '-'}
                        </p>
                        {!isGoogle && (
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {device.userPrincipalName || '-'}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-slate-900 dark:text-white">
                          {isGoogle ? device.deviceType : device.osType}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {isGoogle ? device.os : device.osVersion}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}
                        >
                          <StatusIcon className="h-3.5 w-3.5" />
                          {isGoogle
                            ? (device.status ? device.status.charAt(0).toUpperCase() + device.status.slice(1) : 'Unknown')
                            : device.complianceState === 'compliant'
                              ? 'Compliant'
                              : device.complianceState === 'noncompliant'
                                ? 'Non-Compliant'
                                : (device.complianceState || 'Unknown')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500 dark:text-slate-400">
                        {(isGoogle ? device.lastSyncTime : device.lastSyncDateTime)
                          ? new Date(isGoogle ? device.lastSyncTime : device.lastSyncDateTime).toLocaleDateString()
                          : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
